import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import API_CONFIG from '../config/api';

// Dynamic Links Component for Online Results page
const DynamicLinks: React.FC = () => {
  const [links, setLinks] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_CONFIG.getUrl('links/public/position/online-results'));
      
      if (!response.ok) {
        throw new Error('خطا در دریافت لینک‌ها');
      }
      
      const data = await response.json();
      setLinks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در دریافت لینک‌ها');
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-500 hover:bg-blue-600 text-white',
      green: 'bg-green-500 hover:bg-green-600 text-white',
      purple: 'bg-purple-500 hover:bg-purple-600 text-white',
      red: 'bg-red-500 hover:bg-red-600 text-white',
      yellow: 'bg-yellow-500 hover:bg-yellow-600 text-black',
      indigo: 'bg-indigo-500 hover:bg-indigo-600 text-white',
      pink: 'bg-pink-500 hover:bg-pink-600 text-white',
      gray: 'bg-gray-500 hover:bg-gray-600 text-white'
    };
    return colorMap[color] || 'bg-blue-500 hover:bg-blue-600 text-white';
  };

  const getIcon = (iconName: string) => {
    const iconMap: Record<string, string> = {
      link: '🔗',
      home: '🏠',
      info: 'ℹ️',
      help: '❓',
      contact: '📞',
      about: '👥',
      services: '⚙️',
      news: '📰',
      support: '🆘',
      docs: '📚'
    };
    return iconMap[iconName] || '🔗';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-slate-800 mb-4">لینک‌های مفید</h2>
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-slate-800 mb-4">لینک‌های مفید</h2>
        <div className="text-center text-red-500 py-4">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (links.length === 0) {
    return null; // Don't show the component if no links
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
      <h2 className="text-xl font-bold text-slate-800 mb-4">لینک‌های مفید</h2>
      <div className="flex flex-wrap gap-3">
        {links.map((link) => (
          <a
            key={link._id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg ${getColorClasses(link.color)}`}
            title={link.description || link.title}
          >
            <span className="text-lg">{getIcon(link.icon)}</span>
            <span>{link.title}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

const ViewOnlineResults: React.FC = () => {
  return (
    <DashboardLayout>
      <div className="p-4">
        <div className="card bg-white rounded-sm p-4 border border-slate-300">
          <h2 className="text-lg font-bold text-slate-800 mb-4">مشاهده آنلاین جواب</h2>
          <p className="text-slate-600 mb-4">
            در این بخش می‌توانید نتایج آزمایش‌های خود را به صورت آنلاین مشاهده نمایید.
          </p>

          {/* here will be the online results */}
          <DynamicLinks />
          
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 ml-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-green-700 text-sm">
                نتایج آزمایش‌های تایید شده در این بخش به صورت آنلاین قابل مشاهده است.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="text-center py-12 text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm">
                هنوز نتیجه آزمایشی برای مشاهده آنلاین در دسترس نیست
              </p>
              <p className="text-xs text-gray-400 mt-2">
                نتایج آزمایش‌های تایید شده در این قسمت نمایش داده خواهد شد
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-md">
            <h3 className="text-sm font-medium text-gray-800 mb-2">راهنمای استفاده:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• پس از تایید نتایج توسط آزمایشگاه، نتایج در این بخش نمایش داده می‌شود</li>
              <li>• می‌توانید نتایج را مشاهده کرده و در صورت نیاز پرینت بگیرید</li>
              <li>• برای دریافت فایل PDF نتایج از بخش مربوطه استفاده کنید</li>
            </ul>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewOnlineResults; 